import { MicroservicesSettings } from './types';
import {
    GetAccountFn,
    initGetAccountFn,
} from './domains/account/methods/get-account';
import { initLoginFn, LoginFn } from './domains/authenticate/methods/login';
import { initLogoutFn, LogoutFn } from './domains/authenticate/methods/logout';
import {
    GetOffersCommerceInfoFn,
    initGetOffersCommerceInfoFn,
} from './domains/offers/methods/get-offers-commerce-info';
import {
    ConvertDtokToMagicLinkFn,
    initConvertDtokToMagicLinkFn,
} from './domains/account/methods/convert-dtok-to-magic-link';
import {
    ConvertDtokToMagicLinkCanadaFn,
    initConvertDtokToMagicLinkCanadaFn,
} from './domains/account/methods/convert-dtok-to-magic-link-canada';
import {
    CreateSalIdentityFn,
    initCreateSalIdentityFn,
} from './domains/identity/methods/create-sal-identity';
import {
    CreateSalIdentityCanadaFn,
    initCreateSalIdentityCanadaRequestFn,
} from './domains/identity/methods/create-sal-identity-canada';
import {
    GetAccountByEmailFn,
    initGetAccountByEmailFn,
} from './domains/identity/methods/get-account-by-email';
import { initValidateFn, ValidateFn } from './domains/device/methods/validate';
import { initNonPiiFn, NonPiiFn } from './domains/account/methods/non-pii';
import {
    initLogMessageFn,
    LogMessageFn,
} from './domains/utility/methods/log-message';
import { initVerifyFn, VerifyFn } from './domains/account/methods/verify';
import { InfoFn, initInfoFn } from './domains/device/methods/info';
import {
    initOffersCustomerFn,
    OffersCustomerFn,
} from './domains/offers/methods/offers-customer';
import {
    initOffersRenewalFn,
    OffersRenewalFn,
} from './domains/offers/methods/offers-renewal';
import {
    initGetOffersInfoFn,
    OffersInfoFn,
} from './domains/offers/methods/offers-info';
import { EnvInfoFn, initEnvInfoFn } from './domains/utility/methods/env-info';
import {
    initUpdateUseCaseFn,
    UpdateUseCaseFn,
} from './domains/utility/methods/update-usecase';
import {
    AllPackageDescFn,
    initAllPackageDescFn,
} from './domains/offers/methods/all-package-desc';
import {
    initNextBestActionFn,
    NextBestActionFn,
} from './domains/account/methods/next-best-action';
import { initRefreshFn, RefreshFn } from './domains/device/methods/refresh';
import {
    initValidateLoginFn,
    ValidateLoginFn,
} from './domains/authenticate/methods/validate-login';
import {
    initPasswordFn,
    PasswordFn,
} from './domains/validate/methods/password';
import {
    initUpdateStreamingFn,
    UpdateStreamingFn,
} from './domains/account/methods/update-streaming';
import {
    initUpdateAccountLoginFn,
    UpdateAccountLoginFn,
} from './domains/account-mgmt/methods/update-account-login';
import {
    initModifySubscriptionOptionsFn,
    ModifySubscriptionOptionsFn,
} from './domains/account-mgmt/methods/modify-subscription-options';
import { AcscFn, initAcscFn } from './domains/account/methods/acsc';
import {
    initSwapRadioQuoteFn,
    SwapRadioQuoteFn,
} from './domains/quotes/methods/swap-radio-quote';
import {
    initSwapRadioFn,
    SwapRadioFn,
} from './domains/purchase/methods/swap-radio';
import {
    initOffersCustomerAddFn,
    OffersCustomerAddFn,
} from './domains/offers/methods/offers-customer-add';
import {
    CaptchaFn,
    initCaptchaFn,
} from './domains/check-eligibility/methods/captcha';
import {
    initUniqueLoginFn,
    UniqueLoginFn,
} from './domains/validate/methods/unique-login';
import { FlepzFn, initFlepzFn } from './domains/identity/methods/flepz';
import { initQuoteFn, QuoteFn } from './domains/quotes/methods/quote';
import {
    AddSubscriptionFn,
    initAddSubscriptionFn,
} from './domains/purchase/methods/add-subscription';
import {
    initRemoveDeviceFn,
    RemoveDeviceFn,
} from './domains/account-mgmt/methods/remove-device';
import {
    CustomerInfoFn,
    initCustomerInfoFn,
} from './domains/validate/methods/customer-info';
import { ActivityFn, initActivityFn } from './domains/billing/methods/activity';
import {
    initUpdatePaymentInfoFn,
    UpdatePaymentInfoFn,
} from './domains/payment/methods/update-payment-info';
import {
    initOffersCustomerChangeFn,
    OffersCustomerChangeFn,
} from './domains/offers/methods/offers-customer-change';
import {
    DeviceLicensePlateFn,
    initDeviceLicensePlateFn,
} from './domains/identity/methods/device-license-plate';
import {
    initSendRefreshInstructionFn,
    SendRefreshInstructionFn,
} from './domains/device/methods/send-refresh-instruction';
import {
    EligibleRadiosFn,
    initEligibleRadiosFn,
} from './domains/account/methods/eligible-radios';
import {
    EligibilityCheckFn,
    initEligibilityCheckFn,
} from './domains/account/methods/eligibility-check';
import {
    initLinkDeviceFn,
    LinkDeviceFn,
} from './domains/purchase/methods/link-device';
import { initOemFn, OemFn } from './domains/account/methods/oem';

export interface IMicroservicesSdk {
    domains: MicroserviceDomains;
}

interface MicroserviceDomains {
    account: {
        acsc: AcscFn;
        getAccount: GetAccountFn;
        convertDtokToMagicLink: ConvertDtokToMagicLinkFn;
        convertDtokToMagicLinkCanada: ConvertDtokToMagicLinkCanadaFn;
        eligibilityCheck: EligibilityCheckFn;
        eligibleRadios: EligibleRadiosFn;
        nextBestAction: NextBestActionFn;
        nonPii: NonPiiFn;
        oem: OemFn;
        updateStreaming: UpdateStreamingFn;
        verify: VerifyFn;
    };
    accountManagement: {
        modifySubscriptionOptions: ModifySubscriptionOptionsFn;
        removeDevice: RemoveDeviceFn;
        updateAccountLogin: UpdateAccountLoginFn;
    };
    authenticate: {
        login: LoginFn;
        logout: LogoutFn;
        validateLogin: ValidateLoginFn;
    };
    billing: {
        activity: ActivityFn;
    };
    checkEligibility: {
        captcha: CaptchaFn;
    };
    device: {
        info: InfoFn;
        refresh: RefreshFn;
        sendRefreshInstruction: SendRefreshInstructionFn;
        validate: ValidateFn;
    };
    identity: {
        createSalIdentity: CreateSalIdentityFn;
        createSalIdentityCanada: CreateSalIdentityCanadaFn;
        deviceLicensePlate: DeviceLicensePlateFn;
        flepz: FlepzFn;
        getAccountByEmail: GetAccountByEmailFn;
    };
    offers: {
        allPackageDesc: AllPackageDescFn;
        getOffersCommerceInfo: GetOffersCommerceInfoFn;
        customer: OffersCustomerFn;
        customerAdd: OffersCustomerAddFn;
        customerChange: OffersCustomerChangeFn;
        renewal: OffersRenewalFn;
        info: OffersInfoFn;
    };
    payment: {
        updatePaymentInfo: UpdatePaymentInfoFn;
    };
    purchase: {
        addSubscription: AddSubscriptionFn;
        linkDevice: LinkDeviceFn;
        swapRadio: SwapRadioFn;
    };
    quotes: {
        quote: QuoteFn;
        swapRadioQuote: SwapRadioQuoteFn;
    };
    utility: {
        envInfo: EnvInfoFn;
        logMessage: LogMessageFn;
        updateUseCase: UpdateUseCaseFn;
    };
    validate: {
        customerInfo: CustomerInfoFn;
        password: PasswordFn;
        uniqueLogin: UniqueLoginFn;
    };
}

export class MicroservicesSdk implements IMicroservicesSdk {
    domains: MicroserviceDomains;

    constructor(microservicesSettings: MicroservicesSettings) {
        const baseUrl =
            microservicesSettings.currentCountryCode.toLowerCase() === 'us'
                ? microservicesSettings.baseUrlUS
                : microservicesSettings.baseUrlCanada;
        this.domains = {
            account: {
                acsc: initAcscFn({ baseUrl }),
                getAccount: initGetAccountFn({ baseUrl }),
                convertDtokToMagicLink: initConvertDtokToMagicLinkFn({
                    baseUrl,
                }),
                convertDtokToMagicLinkCanada:
                    initConvertDtokToMagicLinkCanadaFn({
                        baseUrl: microservicesSettings.baseUrlCanada,
                    }),
                eligibilityCheck: initEligibilityCheckFn({ baseUrl }),
                eligibleRadios: initEligibleRadiosFn({ baseUrl }),
                nextBestAction: initNextBestActionFn({ baseUrl }),
                nonPii: initNonPiiFn({ baseUrl }),
                oem: initOemFn({ baseUrl }),
                updateStreaming: initUpdateStreamingFn({ baseUrl }),
                verify: initVerifyFn({ baseUrl }),
            },
            accountManagement: {
                modifySubscriptionOptions: initModifySubscriptionOptionsFn({
                    baseUrl,
                }),
                removeDevice: initRemoveDeviceFn({ baseUrl }),
                updateAccountLogin: initUpdateAccountLoginFn({ baseUrl }),
            },
            authenticate: {
                login: initLoginFn({ baseUrl }),
                logout: initLogoutFn({ baseUrl }),
                validateLogin: initValidateLoginFn({ baseUrl }),
            },
            billing: {
                activity: initActivityFn({ baseUrl }),
            },
            checkEligibility: {
                captcha: initCaptchaFn({ baseUrl }),
            },
            device: {
                info: initInfoFn({ baseUrl }),
                refresh: initRefreshFn({ baseUrl }),
                sendRefreshInstruction: initSendRefreshInstructionFn({
                    baseUrl,
                }),
                validate: initValidateFn({ baseUrl }),
            },
            identity: {
                createSalIdentity: initCreateSalIdentityFn({ baseUrl }),
                createSalIdentityCanada: initCreateSalIdentityCanadaRequestFn({
                    baseUrl: microservicesSettings.baseUrlCanada,
                }),
                deviceLicensePlate: initDeviceLicensePlateFn({ baseUrl }),
                flepz: initFlepzFn({ baseUrl }),
                getAccountByEmail: initGetAccountByEmailFn({ baseUrl }),
            },
            offers: {
                allPackageDesc: initAllPackageDescFn({ baseUrl }),
                getOffersCommerceInfo: initGetOffersCommerceInfoFn({ baseUrl }),
                customer: initOffersCustomerFn({ baseUrl }),
                customerAdd: initOffersCustomerAddFn({ baseUrl }),
                customerChange: initOffersCustomerChangeFn({ baseUrl }),
                renewal: initOffersRenewalFn({ baseUrl }),
                info: initGetOffersInfoFn({ baseUrl }),
            },
            payment: {
                updatePaymentInfo: initUpdatePaymentInfoFn({ baseUrl }),
            },
            purchase: {
                addSubscription: initAddSubscriptionFn({ baseUrl }),
                linkDevice: initLinkDeviceFn({ baseUrl }),
                swapRadio: initSwapRadioFn({ baseUrl }),
            },
            quotes: {
                quote: initQuoteFn({ baseUrl }),
                swapRadioQuote: initSwapRadioQuoteFn({ baseUrl }),
            },
            utility: {
                envInfo: initEnvInfoFn({ baseUrl }),
                logMessage: initLogMessageFn({ baseUrl }),
                updateUseCase: initUpdateUseCaseFn({ baseUrl }),
            },
            validate: {
                customerInfo: initCustomerInfoFn({ baseUrl }),
                password: initPasswordFn({ baseUrl }),
                uniqueLogin: initUniqueLoginFn({ baseUrl }),
            },
        };
    }
}
